import { useState, useEffect } from "react";
import { Phone, Loader2, Euro, CheckCircle, AlertCircle, CreditCard, Hash, Calendar, Lock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Separator } from "@/components/ui/separator";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function MOTOPage() {
  const [amount, setAmount] = useState("");
  const [platformFee, setPlatformFee] = useState("");
  const [orderNumber, setOrderNumber] = useState("");
  const [customerEmail, setCustomerEmail] = useState("");
  const [cardNumber, setCardNumber] = useState("");
  const [expiryMonth, setExpiryMonth] = useState("");
  const [expiryYear, setExpiryYear] = useState("");
  const [cvv, setCvv] = useState("");
  const [loading, setLoading] = useState(false);
  const [lastResult, setLastResult] = useState(null);
  const [processingStep, setProcessingStep] = useState("");

  // Set page title
  useEffect(() => {
    document.title = "MOTO | GeekTickets POS";
  }, []);

  // Format card number with spaces
  const formatCardNumber = (value) => {
    const cleaned = value.replace(/\D/g, "");
    const groups = cleaned.match(/.{1,4}/g);
    return groups ? groups.join(" ") : cleaned;
  };

  const handleCardNumberChange = (e) => {
    const formatted = formatCardNumber(e.target.value);
    if (formatted.replace(/\s/g, "").length <= 19) {
      setCardNumber(formatted);
    }
  };

  const validateForm = () => {
    if (!amount || parseFloat(amount) <= 0) {
      toast.error("Please enter a valid amount");
      return false;
    }
    if (!platformFee || parseFloat(platformFee) < 0) {
      toast.error("Please enter a valid platform fee");
      return false;
    }
    if (parseFloat(platformFee) > parseFloat(amount)) {
      toast.error("Platform fee cannot exceed the total amount");
      return false;
    }
    if (!orderNumber.trim()) {
      toast.error("Please enter an order number");
      return false;
    }
    if (!customerEmail.trim() || !customerEmail.includes("@")) {
      toast.error("Please enter a valid customer email");
      return false;
    }
    const cleanCardNumber = cardNumber.replace(/\s/g, "");
    if (!cleanCardNumber || cleanCardNumber.length < 13 || cleanCardNumber.length > 19) {
      toast.error("Please enter a valid card number");
      return false;
    }
    if (!expiryMonth || parseInt(expiryMonth) < 1 || parseInt(expiryMonth) > 12) {
      toast.error("Please enter a valid expiry month (01-12)");
      return false;
    }
    if (!expiryYear || expiryYear.length < 2) {
      toast.error("Please enter a valid expiry year");
      return false;
    }
    if (!cvv || cvv.length < 3 || cvv.length > 4) {
      toast.error("Please enter a valid CVV (3-4 digits)");
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    setLoading(true);
    setLastResult(null);
    setProcessingStep("Processing payment...");

    const cleanCardNumber = cardNumber.replace(/\s/g, "");

    try {
      // Single API call - backend handles everything
      const response = await axios.post(`${API}/moto/payment`, {
        amount: parseFloat(amount),
        platform_fee: parseFloat(platformFee),
        order_number: orderNumber,
        customer_email: customerEmail,
        card_number: cleanCardNumber,
        expiry_month: expiryMonth.padStart(2, "0"),
        expiry_year: expiryYear,
        cvv: cvv
      });

      setLastResult({
        success: response.data.success,
        ...response.data
      });

      if (response.data.success) {
        toast.success("MOTO payment completed successfully!");
        clearForm();
      } else {
        toast.error(response.data.message || "Payment was not successful");
      }

    } catch (error) {
      console.error("MOTO payment error:", error);
      
      let errorMsg = "Failed to process payment";
      
      if (error.response?.data?.detail) {
        errorMsg = error.response.data.detail;
      } else if (error.response?.data?.errors?.[0]?.message) {
        errorMsg = error.response.data.errors[0].message;
      } else if (error.response?.data?.message) {
        errorMsg = error.response.data.message;
      }
      
      toast.error(errorMsg);
      setLastResult({
        success: false,
        message: errorMsg
      });
    } finally {
      setLoading(false);
      setProcessingStep("");
    }
  };

  const clearForm = () => {
    setAmount("");
    setPlatformFee("");
    setOrderNumber("");
    setCustomerEmail("");
    setCardNumber("");
    setExpiryMonth("");
    setExpiryYear("");
    setCvv("");
    setLastResult(null);
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-2xl mx-auto">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
          <Phone className="w-5 h-5 text-purple-600" />
        </div>
        <div>
          <h1 className="text-xl sm:text-2xl font-heading font-bold">MOTO Payment</h1>
          <p className="text-sm text-muted-foreground">Mail Order / Telephone Order</p>
        </div>
      </div>

      {/* Warning Banner */}
      <Alert className="mb-6 border-purple-200 bg-purple-50">
        <Phone className="h-4 w-4 text-purple-600" />
        <AlertTitle className="text-purple-800">Card Not Present Transaction</AlertTitle>
        <AlertDescription className="text-purple-700 text-sm">
          Use this for phone orders where you manually enter the customer's card details.
          Card numbers are not stored - only the last 5 digits are saved for reference.
        </AlertDescription>
      </Alert>

      {/* Result Alert */}
      {lastResult && (
        <Alert 
          className={`mb-6 ${lastResult.success 
            ? "border-green-200 bg-green-50" 
            : "border-red-200 bg-red-50"
          }`}
        >
          {lastResult.success ? (
            <CheckCircle className="h-4 w-4 text-green-600" />
          ) : (
            <AlertCircle className="h-4 w-4 text-red-600" />
          )}
          <AlertTitle className={lastResult.success ? "text-green-800" : "text-red-800"}>
            {lastResult.success ? "Payment Successful" : "Payment Failed"}
          </AlertTitle>
          <AlertDescription className={lastResult.success ? "text-green-700" : "text-red-700"}>
            {lastResult.success ? (
              <div className="text-sm space-y-1 mt-2">
                <p><strong>Order:</strong> {lastResult.order_number}</p>
                <p><strong>Amount:</strong> €{lastResult.amount?.toFixed(2)}</p>
                <p><strong>Platform Fee:</strong> €{lastResult.platform_fee?.toFixed(2)}</p>
                <p><strong>Card:</strong> ****{lastResult.card_last5}</p>
                <p><strong>Status:</strong> {lastResult.status}</p>
              </div>
            ) : (
              lastResult.message
            )}
          </AlertDescription>
        </Alert>
      )}

      {/* Payment Form */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <CreditCard className="w-5 h-5" />
            Payment Details
          </CardTitle>
          <CardDescription>
            Enter the customer's payment information
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Order Info Section */}
            <div className="space-y-4">
              <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wide">
                Order Information
              </h3>
              
              <div className="space-y-2">
                <Label htmlFor="order-number" className="flex items-center gap-2">
                  <Hash className="w-4 h-4" />
                  Order Number *
                </Label>
                <Input
                  id="order-number"
                  value={orderNumber}
                  onChange={(e) => setOrderNumber(e.target.value)}
                  placeholder="ORD-12345"
                  data-testid="moto-order-number"
                  required
                  disabled={loading}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="customer-email" className="flex items-center gap-2">
                  Customer Email *
                </Label>
                <Input
                  id="customer-email"
                  type="email"
                  value={customerEmail}
                  onChange={(e) => setCustomerEmail(e.target.value)}
                  placeholder="customer@example.com"
                  data-testid="moto-customer-email"
                  required
                  disabled={loading}
                />
              </div>
            </div>

            <Separator />

            {/* Amount Section */}
            <div className="space-y-4">
              <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wide">
                Amount
              </h3>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="amount" className="flex items-center gap-2">
                    <Euro className="w-4 h-4" />
                    Total Amount (EUR) *
                  </Label>
                  <Input
                    id="amount"
                    type="number"
                    step="0.01"
                    min="0.01"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    placeholder="0.00"
                    data-testid="moto-amount"
                    required
                    disabled={loading}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="platform-fee" className="flex items-center gap-2">
                    <Euro className="w-4 h-4" />
                    Platform Fee (EUR) *
                  </Label>
                  <Input
                    id="platform-fee"
                    type="number"
                    step="0.01"
                    min="0"
                    value={platformFee}
                    onChange={(e) => setPlatformFee(e.target.value)}
                    placeholder="0.00"
                    data-testid="moto-platform-fee"
                    required
                    disabled={loading}
                  />
                </div>
              </div>
            </div>

            <Separator />

            {/* Card Details Section */}
            <div className="space-y-4">
              <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wide flex items-center gap-2">
                <Lock className="w-4 h-4" />
                Card Details (Secure)
              </h3>
              
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="card-number" className="flex items-center gap-2">
                    <CreditCard className="w-4 h-4" />
                    Card Number *
                  </Label>
                  <Input
                    id="card-number"
                    value={cardNumber}
                    onChange={handleCardNumberChange}
                    placeholder="4444 3333 2222 1111"
                    data-testid="moto-card-number"
                    className="font-mono tracking-wider"
                    required
                    autoComplete="off"
                    disabled={loading}
                  />
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="expiry-month" className="flex items-center gap-2">
                      <Calendar className="w-4 h-4" />
                      Month *
                    </Label>
                    <Input
                      id="expiry-month"
                      value={expiryMonth}
                      onChange={(e) => {
                        const val = e.target.value.replace(/\D/g, "").slice(0, 2);
                        setExpiryMonth(val);
                      }}
                      placeholder="MM"
                      data-testid="moto-expiry-month"
                      className="font-mono"
                      maxLength={2}
                      required
                      autoComplete="off"
                      disabled={loading}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="expiry-year">Year *</Label>
                    <Input
                      id="expiry-year"
                      value={expiryYear}
                      onChange={(e) => {
                        const val = e.target.value.replace(/\D/g, "").slice(0, 4);
                        setExpiryYear(val);
                      }}
                      placeholder="YY"
                      data-testid="moto-expiry-year"
                      className="font-mono"
                      maxLength={4}
                      required
                      autoComplete="off"
                      disabled={loading}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="cvv" className="flex items-center gap-2">
                      <Lock className="w-4 h-4" />
                      CVV *
                    </Label>
                    <Input
                      id="cvv"
                      type="password"
                      value={cvv}
                      onChange={(e) => {
                        const val = e.target.value.replace(/\D/g, "").slice(0, 4);
                        setCvv(val);
                      }}
                      placeholder="***"
                      data-testid="moto-cvv"
                      className="font-mono"
                      maxLength={4}
                      required
                      autoComplete="off"
                      disabled={loading}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Processing Indicator */}
            {loading && processingStep && (
              <div className="flex items-center gap-2 text-sm text-purple-600 bg-purple-50 p-3 rounded-lg">
                <Loader2 className="w-4 h-4 animate-spin" />
                {processingStep}
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-3 pt-4">
              <Button
                type="submit"
                disabled={loading}
                className="flex-1 bg-purple-600 hover:bg-purple-700"
                data-testid="moto-submit-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Processing...
                  </>
                ) : (
                  <>
                    <CreditCard className="w-4 h-4 mr-2" />
                    Process Payment
                  </>
                )}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={clearForm}
                disabled={loading}
                data-testid="moto-clear-btn"
              >
                Clear Form
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      {/* Security Notice */}
      <div className="mt-6 p-4 bg-muted/50 rounded-lg">
        <div className="flex items-start gap-3">
          <Lock className="w-5 h-5 text-muted-foreground mt-0.5" />
          <div className="text-sm text-muted-foreground">
            <p className="font-medium text-foreground mb-1">PCI Compliance Notice</p>
            <p>
              Card details are transmitted securely to the payment processor and are not stored in our system.
              Only the last 5 digits of the card number are retained for reference purposes.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
